/** Where a visitor came from, decided once on the landing page and then kept, so
 * a lead can say "Bing ad" or "ChatGPT" months later.
 *
 * Two values, because one is never enough: `traffic_source` is *who* sent them
 * (bing, google, chatgpt, linkedin) and `traffic_channel` is *how* (paid_search,
 * organic_search, llm, social, referral, direct). "Bing" alone cannot tell a paid
 * click from an organic result, and those are different budgets.
 *
 * Attribution model is **last non-direct click**, the same convention GA4 uses:
 * a new identifiable source overwrites the stored one, while arriving directly
 * (or from our own pages) leaves the previous source in place — otherwise every
 * internal navigation would erase the ad that paid for the visit.
 *
 * Consent: functional, like the visitor ID and the journey. A source category is
 * not a personal identifier, but it is stored, so it follows the same rule.
 */
import { getVisitorId } from "./adClickIds";

const STORAGE_KEY = "oh_source";
/** Readable by GTM's 1st-Party Cookie variable as `source/channel`. */
const COOKIE = "oh_src";
/** 90 days — matches the click-ID window and GA4's default acquisition window. */
const MAX_AGE_MS = 90 * 24 * 60 * 60 * 1000;

export const TRAFFIC_CHANNELS = {
  PAID_SEARCH: "paid_search",
  ORGANIC_SEARCH: "organic_search",
  LLM: "llm",
  SOCIAL: "social",
  REFERRAL: "referral",
  DIRECT: "direct",
} as const;
export type TrafficChannel = (typeof TRAFFIC_CHANNELS)[keyof typeof TRAFFIC_CHANNELS];

/** Hostname fragment -> source name. Order matters only within a list. */
const LLM_HOSTS: Array<[RegExp, string]> = [
  [/(^|\.)chatgpt\.com$|(^|\.)openai\.com$/, "chatgpt"],
  [/(^|\.)perplexity\.ai$/, "perplexity"],
  [/(^|\.)claude\.ai$|(^|\.)anthropic\.com$/, "claude"],
  [/(^|\.)gemini\.google\.com$|(^|\.)bard\.google\.com$/, "gemini"],
  [/(^|\.)copilot\.microsoft\.com$/, "copilot"],
  [/(^|\.)you\.com$/, "you"],
  [/(^|\.)poe\.com$/, "poe"],
  [/(^|\.)phind\.com$/, "phind"],
  [/(^|\.)deepseek\.com$/, "deepseek"],
  [/(^|\.)grok\.com$|(^|\.)x\.ai$/, "grok"],
  [/(^|\.)meta\.ai$/, "meta_ai"],
  [/(^|\.)mistral\.ai$/, "mistral"],
];

const SEARCH_HOSTS: Array<[RegExp, string]> = [
  [/(^|\.)google\.[a-z.]+$/, "google"],
  [/(^|\.)bing\.com$/, "bing"],
  [/(^|\.)duckduckgo\.com$/, "duckduckgo"],
  [/(^|\.)yahoo\.[a-z.]+$/, "yahoo"],
  [/(^|\.)yandex\.[a-z.]+$/, "yandex"],
  [/(^|\.)baidu\.com$/, "baidu"],
  [/(^|\.)ecosia\.org$/, "ecosia"],
  [/(^|\.)brave\.com$/, "brave"],
  [/(^|\.)startpage\.com$/, "startpage"],
  [/(^|\.)naver\.com$/, "naver"],
];

const SOCIAL_HOSTS: Array<[RegExp, string]> = [
  [/(^|\.)facebook\.com$|(^|\.)fb\.me$/, "facebook"],
  [/(^|\.)instagram\.com$/, "instagram"],
  [/(^|\.)linkedin\.com$|(^|\.)lnkd\.in$/, "linkedin"],
  [/(^|\.)twitter\.com$|(^|\.)x\.com$|(^|\.)t\.co$/, "x"],
  [/(^|\.)reddit\.com$/, "reddit"],
  [/(^|\.)pinterest\.[a-z.]+$/, "pinterest"],
  [/(^|\.)youtube\.com$|(^|\.)youtu\.be$/, "youtube"],
  [/(^|\.)tiktok\.com$/, "tiktok"],
  [/(^|\.)whatsapp\.com$/, "whatsapp"],
];

export type TrafficSource = {
  source: string;
  channel: TrafficChannel;
  /** The referring hostname, kept for debugging an unexpected classification. */
  referrer: string;
};

type Stored = TrafficSource & { t: number };

const match = (host: string, table: Array<[RegExp, string]>): string =>
  table.find(([re]) => re.test(host))?.[1] ?? "";

function hostOf(url: string): string {
  try {
    return new URL(url).hostname.toLowerCase();
  } catch {
    return "";
  }
}

/**
 * Classify the current arrival. Exported so it can be tested directly: the whole
 * point of this file is the decision table, and that is worth asserting.
 */
export function detectTrafficSource(href: string, referrer: string): TrafficSource {
  const params = new URLSearchParams(hostOf(href) ? new URL(href).search : "");
  const get = (k: string) => params.get(k) || params.get(`utm_${k}`) || "";
  const referrerHost = hostOf(referrer);

  // 1. Ad click IDs are proof of a paid click and outrank everything else.
  if (get("msclkid")) return { source: "bing", channel: TRAFFIC_CHANNELS.PAID_SEARCH, referrer: referrerHost };
  if (get("gclid") || get("gbraid") || get("wbraid")) {
    return { source: "google", channel: TRAFFIC_CHANNELS.PAID_SEARCH, referrer: referrerHost };
  }

  // 2. An explicit utm_source is a deliberate statement by whoever built the link.
  const utmSource = params.get("utm_source");
  if (utmSource) {
    const medium = (params.get("utm_medium") || "").toLowerCase();
    const named = match(utmSource.toLowerCase(), LLM_HOSTS);
    const channel: TrafficChannel = named
      ? TRAFFIC_CHANNELS.LLM
      : /cpc|ppc|paid/.test(medium)
        ? TRAFFIC_CHANNELS.PAID_SEARCH
        : /organic/.test(medium)
          ? TRAFFIC_CHANNELS.ORGANIC_SEARCH
          : /social/.test(medium)
            ? TRAFFIC_CHANNELS.SOCIAL
            : TRAFFIC_CHANNELS.REFERRAL;
    return { source: named || utmSource.toLowerCase(), channel, referrer: referrerHost };
  }

  // 3. Referrer-based classification.
  if (!referrerHost || /(^|\.)office-hub\.com$/.test(referrerHost) || referrerHost === "localhost") {
    return { source: "direct", channel: TRAFFIC_CHANNELS.DIRECT, referrer: referrerHost };
  }
  const llm = match(referrerHost, LLM_HOSTS);
  if (llm) return { source: llm, channel: TRAFFIC_CHANNELS.LLM, referrer: referrerHost };

  const search = match(referrerHost, SEARCH_HOSTS);
  if (search) return { source: search, channel: TRAFFIC_CHANNELS.ORGANIC_SEARCH, referrer: referrerHost };

  const social = match(referrerHost, SOCIAL_HOSTS);
  if (social) return { source: social, channel: TRAFFIC_CHANNELS.SOCIAL, referrer: referrerHost };

  return { source: referrerHost, channel: TRAFFIC_CHANNELS.REFERRAL, referrer: referrerHost };
}

function read(): Stored | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const stored = JSON.parse(raw) as Stored;
    return Date.now() - stored.t < MAX_AGE_MS ? stored : null;
  } catch {
    return null;
  }
}

function writeCookie(value: string): void {
  const domain = /(^|\.)office-hub\.com$/.test(window.location.hostname)
    ? ";domain=.office-hub.com"
    : "";
  const secure = window.location.protocol === "https:" ? ";Secure" : "";
  document.cookie = `${COOKIE}=${encodeURIComponent(value)};max-age=${Math.floor(MAX_AGE_MS / 1000)};path=/${domain}${secure};SameSite=Lax`;
}

/**
 * Record this arrival and return the source that now counts. Called once per page
 * load from the root layout, next to the journey.
 */
export function recordTrafficSource(): TrafficSource | null {
  if (typeof window === "undefined") return null;
  if (!getVisitorId()) return null; // no functional consent

  const detected = detectTrafficSource(window.location.href, document.referrer);
  const stored = read();

  // Last NON-DIRECT click wins: a direct arrival must not erase the ad that paid
  // for the visit, and neither must navigating between our own pages.
  const keepStored = detected.channel === TRAFFIC_CHANNELS.DIRECT && stored;
  const current = keepStored ? { source: stored.source, channel: stored.channel, referrer: stored.referrer } : detected;

  if (!keepStored) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...detected, t: Date.now() }));
    } catch {
      // Private mode: the cookie below still carries it for this visit.
    }
    writeCookie(`${detected.source}/${detected.channel}`);
  }
  return current;
}

/** For lead payloads and the dataLayer. Empty object when nothing is known. */
export function getTrafficSource(): Partial<Record<"traffic_source" | "traffic_channel" | "traffic_referrer", string>> {
  if (typeof window === "undefined") return {};
  if (!getVisitorId()) return {};
  const stored = read();
  if (!stored) return {};
  return {
    traffic_source: stored.source,
    traffic_channel: stored.channel,
    traffic_referrer: stored.referrer,
  };
}

/** `bing/paid_search` — one CRM field in the familiar source/medium shape. */
export function getTrafficSourceString(): string {
  const { traffic_source, traffic_channel } = getTrafficSource();
  return traffic_source ? `${traffic_source}/${traffic_channel}` : "";
}

/** Called when functional cookies are withdrawn. */
export function clearTrafficSource(): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing stored to remove.
  }
  const domain = /(^|\.)office-hub\.com$/.test(window.location.hostname)
    ? ";domain=.office-hub.com"
    : "";
  document.cookie = `${COOKIE}=;max-age=0;path=/${domain};SameSite=Lax`;
}
